import React, { forwardRef, type SyntheticEvent, useCallback } from 'react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import AutomationIcon from '@atlaskit/icon/core/automation';
import Tooltip from '@atlaskit/tooltip';
import { useIntl } from '@atlassian/jira-intl/src/index.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { ThemedButton } from '@atlassian/jira-project-theme-components/src/ui/themed-button/ThemedButton.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import messages from './messages.tsx';
import type { Props } from './types.tsx';

const MenuButton = forwardRef<HTMLElement, Props>(({ isSelected, onClick }: Props, ref) => {
	const automationIconTestId = 'automation-menu.ui.button.icon';
	const onClickWithAnalytics = useCallback(
		(event: SyntheticEvent<HTMLElement>, analytics: UIAnalyticsEvent) => {
			fireUIAnalytics(analytics, 'automationMenuButton');
			onClick();
		},
		[onClick],
	);

	const { formatMessage } = useIntl();

	return (
		<Tooltip
			content={formatMessage(messages.menuButtonTooltip)}
			position="top"
			isScreenReaderAnnouncementDisabled
		>
			<ThemedButton
				ref={ref}
				appearance={isVisualRefreshEnabled() ? 'default' : 'subtle'}
				onClick={onClickWithAnalytics}
				isSelected={isSelected}
				testId="automation-menu.ui.button.menu-button"
				id="automation-menu.ui.popup"
				data-vc="automation-menu-button"
				// @ts-expect-error - externalId does not exist on Button types
				externalId="automation-menu.ui.button.menu-button"
				aria-label={formatMessage(messages.menuButtonTooltip)}
				aria-expanded={isSelected}
				iconBefore={
					<AutomationIcon
						label=""
						color="currentColor"
						spacing="spacious"
						testId={automationIconTestId}
					/>
				}
			/>
		</Tooltip>
	);
});

export default MenuButton;
